import React, { useMemo } from "react";
import Highcharts from "../../config/highcharts-config";
import HighchartsReact from "highcharts-react-official";
import { useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  BarChartConfig,
  getCategoryRollupScope,
} from "@store/slices/chart-config-slice";
import type { WidgetRollup } from "@models/Rollup";
import { useVisualizationBaseDisplayName } from "@hooks/use-visualization-base-display-name";
import { Box, Typography } from "@mui/material";
import {
  getMissingDataWarning,
  getPeriodTypeLabel,
  getRollingAverageFooterText,
} from "@/utils/rolling-average-utils";
import { getTimePeriodNominalRepresentation } from "@/utils/time-period-display";
import { resolveTimePeriodInWindow } from "@/utils/time-period-utils";
import type { TimePeriod } from "@store/slices/chart-config-slice";
import { applyTopN } from "@/utils/chart-utils";

interface ConfigurableBarChartProps {
  config?: BarChartConfig;
  width?: string | number;
  height?: string | number;
  dashboardDateOverride?: { startDate: string; endDate: string };
}

const ConfigurableBarChart: React.FC<ConfigurableBarChartProps> = ({
  config: propConfig,
  width = "100%",
  height = "100%",
  dashboardDateOverride,
}) => {
  const { currentConfig, datasetId } = useAppSelector(chartConfigSelector);
  const config = propConfig || currentConfig;

  // Live-resolved base name so the preview footer shows the (pre-configured) base.
  const baseDisplayName = useVisualizationBaseDisplayName(
    datasetId,
    config?.visualizationBase,
  );

  // Calculate missing data warning when rolling window extends before dataset start
  const missingDataWarning = useMemo(() => {
    if (!config?.rollingAverage?.enabled || !config.rollingAverage.windowSize) {
      return null;
    }

    const timePeriod = config?.timePeriodSelection?.timePeriod;
    if (!timePeriod) return null;

    return getMissingDataWarning(
      config.rollingAverage.windowSize,
      config?.effectiveRollingPeriodCount,
      config?.missingRollingPeriodLabels,
    );
  }, [
    config?.rollingAverage,
    config?.effectiveRollingPeriodCount,
    config?.missingRollingPeriodLabels,
    config?.timePeriodSelection?.timePeriod,
  ]);

  const isStatTestingEnabled = config?.statisticalTesting?.enabled;

  // Calculate dashboard filter note (Module 4)
  const dashboardFilterNote = useMemo(() => {
    if (!dashboardDateOverride?.startDate || !dashboardDateOverride?.endDate) {
      return null;
    }
    const timePeriod = config?.timePeriodSelection?.timePeriod as
      | TimePeriod
      | undefined;
    if (
      !timePeriod ||
      timePeriod === "all_time" ||
      timePeriod === "custom_period"
    ) {
      return `Time Period (filtered): ${dashboardDateOverride.startDate} – ${dashboardDateOverride.endDate}`;
    }
    const resolved = resolveTimePeriodInWindow(
      timePeriod,
      dashboardDateOverride.startDate,
      dashboardDateOverride.endDate,
    );
    if (!resolved) {
      const unit = timePeriod.includes("month")
        ? "month"
        : timePeriod.includes("semester")
          ? "semester"
          : "quarter";
      return `No complete ${unit} available within the selected date range.`;
    }
    const label = getTimePeriodNominalRepresentation(
      timePeriod,
      resolved.startDate,
      resolved.endDate,
    );

    // If rolling average is enabled, show rolling average footer format
    if (config?.rollingAverage?.enabled && config.rollingAverage.windowSize) {
      const windowSize =
        config.effectiveRollingPeriodCount ?? config.rollingAverage.windowSize;
      const rollingText = getRollingAverageFooterText(
        windowSize,
        timePeriod,
        resolved.startDate,
        resolved.endDate,
      );
      // Replace "Rolling Average:" with "Rolling Average (filtered):"
      return rollingText.replace(
        "Rolling Average:",
        "Rolling Average (filtered):",
      );
    }

    // If stat testing is enabled, show "X (filtered): A vs B" for all named period
    // types. The comparison period is always the complete period immediately before
    // the displayed period — per PRD-35 it is allowed to be outside the filter range.
    if (isStatTestingEnabled) {
      const periodLabels: Partial<Record<TimePeriod, string>> = {
        latest_month: "Latest Month",
        previous_month: "Previous Month",
        latest_quarter: "Latest Quarter",
        previous_quarter: "Previous Quarter",
        latest_semester: "Latest Semester",
        previous_semester: "Previous Semester",
      };
      const periodLabel = periodLabels[timePeriod];

      if (periodLabel) {
        const dayBefore = new Date(resolved.startDate);
        dayBefore.setDate(dayBefore.getDate() - 1);
        const dayBeforeStr = dayBefore.toISOString().split("T")[0];

        const comparisonType = (
          timePeriod.includes("month")
            ? "latest_month"
            : timePeriod.includes("semester")
              ? "latest_semester"
              : "latest_quarter"
        ) as TimePeriod;
        const comparisonResolved = resolveTimePeriodInWindow(
          comparisonType,
          "2000-01-01",
          dayBeforeStr,
        );

        if (comparisonResolved) {
          const comparisonLabel = getTimePeriodNominalRepresentation(
            comparisonType,
            comparisonResolved.startDate,
            comparisonResolved.endDate,
          );
          if (comparisonLabel) {
            return `${periodLabel} (filtered): ${label} vs ${comparisonLabel}`;
          }
        }
      }
    }

    return label ? `Time Period (filtered): ${label}` : null;
  }, [
    dashboardDateOverride,
    config?.timePeriodSelection?.timePeriod,
    config?.rollingAverage,
    config?.effectiveRollingPeriodCount,
    isStatTestingEnabled,
  ]);

  const options = useMemo<Highcharts.Options>(() => {
    // Determine chart type based on orientation
    const chartType = config.orientation === "horizontal" ? "bar" : "column";

    // Apply data sorting if needed
    let sortedCategories = [...config.categories];
    let sortedSeries = config.series.map((series) => ({
      ...series,
      data: [...series.data],
      colors: series.colors ? [...series.colors] : undefined,
    }));

    if (
      (config.dataSorting === "ascending" || config.dataSorting === "descending") &&
      config.series.length > 0
    ) {
      // Check if we have multiple series with single data points (typical bar chart structure)
      // In this case, each series represents a category, not multiple data points
      const isCategoryPerSeries = config.series.every(
        (s) => s.data.length === 1,
      );

      if (isCategoryPerSeries) {
        // Sort series themselves (each series is a category)
        const seriesWithIndex = config.series.map((series, index) => ({
          series: {
            ...series,
            data: [...series.data],
            colors: series.colors ? [...series.colors] : undefined,
          },
          value: series.data[0],
          index,
        }));

        seriesWithIndex.sort((a, b) =>
          config.dataSorting === "ascending"
            ? a.value - b.value
            : b.value - a.value,
        );

        // Update sorted series - create new objects to avoid mutation
        sortedSeries = seriesWithIndex.map((item) => ({
          ...item.series,
          data: [...item.series.data],
          colors: item.series.colors ? [...item.series.colors] : undefined,
        }));
        // Categories remain the same (single category)
      } else {
        // Original logic: Sort data points within series
        const firstSeriesData = [...config.series[0].data];
        const pairs = firstSeriesData.map((value, index) => ({
          value,
          category: sortedCategories[index],
          index,
        }));

        pairs.sort((a, b) =>
          config.dataSorting === "ascending"
            ? a.value - b.value
            : b.value - a.value,
        );

        // Apply the same sorting order to all series, including colors
        sortedCategories = pairs.map((p) => p.category);
        sortedSeries = config.series.map((series) => ({
          ...series,
          data: pairs.map((p) => series.data[p.index]),
          colors: series.colors
            ? pairs.map((p) => series.colors![p.index])
            : undefined,
        }));
      }
    }

    // Apply Top N filter if enabled (hidden bars are excluded from the pool)
    const topN = applyTopN(sortedSeries, sortedCategories, config);
    sortedSeries = topN.series;
    sortedCategories = topN.categories;

    // Apply bar customization: custom order, pins, per-bar colors, visibility, label overrides.
    const bc = config.barCustomization;
    if (bc) {
      const isCatPerSeriesCheck = sortedSeries.every((s) => s.data.length === 1);

      if (isCatPerSeriesCheck) {
        if (config.dataSorting === "custom" && bc.customOrder.length > 0) {
          const orderIndex = new Map(
            bc.customOrder.map((n: string, i: number) => [n, i]),
          );
          sortedSeries = [...sortedSeries].sort((a, b) => {
            const ai = orderIndex.has(a.name) ? orderIndex.get(a.name)! : sortedSeries.length;
            const bi = orderIndex.has(b.name) ? orderIndex.get(b.name)! : sortedSeries.length;
            return ai - bi;
          });
        } else if (bc.pinnedFirst || bc.pinnedLast) {
          const firstBar = bc.pinnedFirst
            ? sortedSeries.find((s) => s.name === bc.pinnedFirst)
            : null;
          const lastBar = bc.pinnedLast
            ? sortedSeries.find((s) => s.name === bc.pinnedLast)
            : null;
          const middle = sortedSeries.filter(
            (s) => s.name !== bc.pinnedFirst && s.name !== bc.pinnedLast,
          );
          sortedSeries = [
            ...(firstBar ? [firstBar] : []),
            ...middle,
            ...(lastBar ? [lastBar] : []),
          ];
        }
      }

      if (
        config.colorPalette === "Custom (per-bar)" &&
        Object.keys(bc.manualColors).length > 0
      ) {
        sortedSeries = sortedSeries.map((s) => ({
          ...s,
          color: bc.manualColors[s.name] ?? s.color,
        }));
      }

      if (bc.hiddenBars.length > 0) {
        const visible = sortedSeries.filter(
          (s) => !bc.hiddenBars.includes(s.name),
        );
        if (visible.length > 0) sortedSeries = visible;
      }

      if (Object.keys(bc.labelOverrides).length > 0) {
        sortedSeries = sortedSeries.map((s) => ({
          ...s,
          name: bc.labelOverrides[s.name] ?? s.name,
        }));
      }
    }

    // Configure data labels (values) based on value placement
    const shouldShowDataLabels = config.valuePlacement !== "none";
    const isCategoryPerSeries = config.series.every((s) => s.data.length === 1);
    const isMultiColorPalette =
      config.colorPalette === "Multicolor" ||
      config.colorPalette === "Gradient" ||
      config.colorPalette === "Grayscale" ||
      config.colorPalette === "Custom (per-bar)";

    let dataLabelsAlign = "center";
    let dataLabelsVerticalAlign = "middle";
    let dataLabelsInside = false;

    if (config.valuePlacement === "end") {
      // End of bar (outside)
      if (config.orientation === "horizontal") {
        dataLabelsAlign = "left";
      } else {
        dataLabelsVerticalAlign = "bottom";
      }
      dataLabelsInside = false;
    } else if (config.valuePlacement === "inside") {
      // Inside of bar
      dataLabelsAlign = "center";
      dataLabelsVerticalAlign = "middle";
      dataLabelsInside = true;
    }

    // Helper function to get arrow indicator based on statSignificance
    const getSignificanceArrow = (statSignificance?: -1 | 0 | 1): string => {
      if (statSignificance === 1) {
        return ' <span style="color: #10B981;">▲</span>'; // Green up arrow
      } else if (statSignificance === -1) {
        return ' <span style="color: #EF4444;">▼</span>'; // Red down arrow
      }
      return ""; // No arrow for 0 or undefined
    };

    const showBarN = config.showBarN ?? false;
    const isHorizontal = config.orientation === "horizontal";

    const getCellN = (seriesIndex: number, pointIndex: number): number | null => {
      if (!isCategoryPerSeries) return null;
      const src = !config.showLegend
        ? sortedSeries[pointIndex]
        : sortedSeries[seriesIndex];
      return (src as any)?.cellN ?? null;
    };

    const buildArrow = (seriesIndex: number, pointIndex: number): string => {
      if (!config.statisticalTesting?.enabled) return "";
      let statSig: number | undefined;
      if (isCategoryPerSeries && !config.showLegend) {
        const s = sortedSeries[pointIndex];
        if (s && "statSignificance" in s) statSig = (s as any).statSignificance;
      } else {
        const s = sortedSeries[seriesIndex];
        if (s && "statSignificance" in s) statSig = (s as any).statSignificance;
      }
      return statSig !== undefined ? getSignificanceArrow(statSig as -1 | 0 | 1) : "";
    };

    // Horizontal N-only: value placement is "Don't display" but Bar N is on.
    // Override to end-positioning so the N appears after the bar tip.
    const horizontalNOnly = isHorizontal && showBarN && !shouldShowDataLabels;

    const primaryDataLabel: Highcharts.DataLabelsOptions = {
      enabled: shouldShowDataLabels || (isHorizontal && showBarN),
      align: (horizontalNOnly ? "left" : dataLabelsAlign) as Highcharts.AlignValue,
      verticalAlign: (horizontalNOnly ? "middle" : dataLabelsVerticalAlign) as Highcharts.VerticalAlignValue,
      inside: horizontalNOnly ? false : dataLabelsInside,
      useHTML: true,
      y: !isHorizontal && showBarN && shouldShowDataLabels ? -16 : 0,
      formatter: function (this: any) {
        const percentage = this.y.toFixed(config.percentageFormatting);
        const si = this.series.index;
        const pi = this.point.index;
        const arrow = buildArrow(si, pi);

        if (isHorizontal && showBarN) {
          const cellN = getCellN(si, pi);
          if (!shouldShowDataLabels) {
            // "Don't display" value placement — show only N
            return cellN != null
              ? `<span style="color:#666;font-size:10px;">${cellN.toLocaleString()}</span>`
              : null;
          }
          const nPart = cellN != null
            ? `<span style="color:#666;font-size:10px;margin-left:6px;">${cellN.toLocaleString()}</span>`
            : "";
          return `${arrow}${percentage}%${nPart}`;
        }

        return `${percentage}%${arrow}`;
      },
      allowOverlap: true,
      style: {
        fontSize: config.compactMode ? "10px" : "12px",
        fontWeight: "normal",
        color: "#333333",
        textOutline: dataLabelsInside ? "1px contrast" : "none",
      },
    };

    const nDataLabel: Highcharts.DataLabelsOptions = {
      enabled: showBarN && !isHorizontal,
      align: dataLabelsAlign as Highcharts.AlignValue,
      verticalAlign: dataLabelsVerticalAlign as Highcharts.VerticalAlignValue,
      inside: dataLabelsInside,
      y: 0,
      formatter: function (this: any) {
        const cellN = getCellN(this.series.index, this.point.index);
        return cellN != null ? cellN.toLocaleString() : null;
      },
      allowOverlap: true,
      style: {
        fontSize: "10px",
        fontWeight: "normal",
        color: "#666666",
        textOutline: "none",
      },
    };

    const dataLabelsConfig = [primaryDataLabel, nDataLabel];

    // Rollup tooltip helpers: build lookups from active scope's rollups.
    const rollupScope = getCategoryRollupScope(config.focusMode);
    const activeCategoryRollups: WidgetRollup[] =
      config.categoryRollupsByScope?.[rollupScope] ?? [];

    const rollupsByDisplayName = new Map<string, WidgetRollup>();
    for (const r of activeCategoryRollups) {
      if (r.name) {
        rollupsByDisplayName.set(
          config.barCustomization?.labelOverrides[r.name] ?? r.name,
          r,
        );
      }
    }

    const buildRollupSourceLine = (rollup: WidgetRollup): string => {
      const labels = rollup.sourceCategoryLabels.map((id) => {
        const originalName = config.variableIdLabelMap?.[id];
        if (originalName) {
          return config.barCustomization?.labelOverrides[originalName] ?? originalName;
        }
        return id;
      });
      return `<span style="font-size:10px;color:#555;">Combines: ${labels.join(", ")}</span>`;
    };

    return {
      chart: {
        type: chartType,
        backgroundColor: "transparent",
        height: typeof height === "number" ? height : undefined,
      },
      title: {
        text: undefined, // Title displayed in drawer header, not in chart
      },
      subtitle: {
        text: undefined, // Subtitle displayed in drawer header, not in chart
      },
      xAxis: {
        // For category-per-series (each series is a bar), use series names as categories
        categories: isCategoryPerSeries
          ? sortedSeries.map((s) =>
              config.truncateCategoryText && s.name.length > 40
                ? s.name.substring(0, 40) + "..."
                : s.name,
            )
          : config.truncateCategoryText
            ? sortedCategories.map((cat) =>
                cat.length > 40 ? cat.substring(0, 40) + "..." : cat,
              )
            : sortedCategories,
        crosshair: true,
        labels: {
          // Always show xAxis labels when labelsPlacement is "next"
          enabled: config.labelsPlacement === "next",
          style: {
            fontSize: config.compactMode ? "10px" : "12px",
          },
        },
      },
      yAxis: {
        min: 0,
        visible: config.showValueAxis,
        title: {
          text: "Percentage (%)",
        },
        labels: {
          format: `{value:.${config.percentageFormatting}f}%`,
          style: {
            fontSize: config.compactMode ? "10px" : "12px",
          },
        },
      },
      tooltip: {
        shared: true,
        outside: true, // Render tooltip at document body level to avoid clipping by overflow:hidden
        style: { zIndex: 9999 }, // Must exceed MUI Drawer z-index (1200) to render above it
        formatter: function () {
          let content = "";

          if (this.points) {
            const barName = this.points[0].key as string;
            const rollup = rollupsByDisplayName.get(barName);
            content += `<span style="font-size:10px">${barName}</span><br/>`;

            this.points.forEach((point: any) => {
              const percentage = point.y?.toFixed(config.percentageFormatting);

              if (
                config?.rollingAverage?.enabled &&
                config.rollingAverage.windowSize
              ) {
                content += `${config.rollingAverage.windowSize}-Period Rolling Average <b>${percentage}%</b>`;
              } else if (config.focusRollupName) {
                content += `Rollup: ${config.focusRollupName}<br/><b>${percentage}%</b>`;
              } else {
                content += `<b>${percentage}%</b>`;
              }
              content += "<br/>";
            });

            if (rollup) {
              content += buildRollupSourceLine(rollup);
            }
          } else if (this.point) {
            const point = this.point as any;
            const barName: string = point.name ?? point.category ?? "";
            const rollup = rollupsByDisplayName.get(barName);
            const percentage = point.y?.toFixed(config.percentageFormatting);

            content += `<span style="font-size:10px">${barName}</span><br/>`;

            if (
              config?.rollingAverage?.enabled &&
              config.rollingAverage.windowSize
            ) {
              content += `${config.rollingAverage.windowSize}-Period Rolling Average <b>${percentage}%</b>`;
            } else if (config.focusRollupName) {
              content += `Rollup: ${config.focusRollupName}<br/><b>${percentage}%</b>`;
            } else {
              content += `<b>${percentage}%</b>`;
            }

            if (rollup) {
              content += "<br/>" + buildRollupSourceLine(rollup);
            }
          }

          return content;
        },
        useHTML: true,
      },
      plotOptions: {
        [chartType]: {
          pointPadding: config.compactMode ? 0.02 : 0.1,
          groupPadding: config.compactMode ? 0.05 : 0.2,
          borderWidth: 0,
          grouping: false, // Disable grouping to prevent bars from getting narrower
          dataLabels: dataLabelsConfig,
        },
        series: {
          animation: { duration: 300 },
          states: {
            hover: {
              brightness: 0.1,
            },
          },
        },
      },
      legend: {
        enabled: isCategoryPerSeries
          ? isMultiColorPalette
            ? config.showLegend // For multi-color: only show if toggle is ON (color legend)
            : config.labelsPlacement === "next" // For single-color: show category text
          : config.series.length > 1,
        itemStyle: {
          fontSize: config.compactMode ? "10px" : "12px",
        },
        align: "center",
        verticalAlign: "bottom",
      },
      credits: { enabled: false },
      exporting: {
        enabled: false,
      },
      accessibility: {
        enabled: true,
        ...(showBarN && {
          point: {
            descriptionFormatter: function (point: any) {
              const name = point.name || point.series?.name || "";
              const pct =
                point.y != null
                  ? `${point.y.toFixed(config.percentageFormatting)}%`
                  : "";
              let cellN: number | null | undefined = undefined;
              if (isCategoryPerSeries) {
                const src = !config.showLegend
                  ? sortedSeries[point.index]
                  : sortedSeries[point.series?.index ?? 0];
                cellN = (src as any)?.cellN ?? null;
              }
              const nSuffix =
                cellN != null ? `, N = ${cellN.toLocaleString()}` : "";
              return `${name}, ${pct}${nSuffix}`;
            },
          },
        }),
      },
      series: isCategoryPerSeries
        ? // For category-per-series: use different structure based on showLegend
          isMultiColorPalette && config.showLegend
          ? // When showLegend is ON: create separate series for each bar to show in legend
            sortedSeries.map((s, index) => ({
              type: chartType as "bar" | "column",
              name: s.name,
              data: [
                {
                  y: s.data[0],
                  x: index,
                  color: s.color || "#57bfe7",
                },
              ],
              color: s.color || "#57bfe7",
              showInLegend: true,
            }))
          : // When showLegend is OFF: use single series to maintain bar width
            [
              {
                type: chartType as "bar" | "column",
                name: config.title || "Data",
                data: sortedSeries.map((s) => ({
                  y: s.data[0],
                  name: s.name,
                  color: s.color || "#57bfe7",
                })),
                showInLegend: false,
                colorByPoint: isMultiColorPalette,
                colors: isMultiColorPalette
                  ? sortedSeries.map((s) => s.color || "#57bfe7")
                  : undefined,
                color: !isMultiColorPalette
                  ? sortedSeries[0]?.color || "#57bfe7"
                  : undefined,
              },
            ]
        : // For data-per-series: keep original structure
          (sortedSeries.map((series) => ({
            type: chartType as "bar" | "column",
            name: series.name,
            data: series.data,
            color: series.color || "#57bfe7",
            colorByPoint: !!series.colors,
            colors: series.colors,
          })) as Highcharts.SeriesOptionsType[]),
    };
  }, [config, height]);

  const showChartN = config.showChartN ?? false;
  const chartNValue = config.chartN ?? null;

  return (
    <div
      style={{
        width,
        height: typeof height === "number" ? `${height}px` : height,
        minHeight: config.compactMode ? 300 : 400,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ flex: 1, minHeight: 0 }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { width: "100%", height: "100%" } }}
        />
      </div>

      {config?.visualizationBase?.enabled && baseDisplayName && (
        <Box
          sx={{
            mt: 1,
            px: 2.5,
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Base: {baseDisplayName}
          </Typography>
        </Box>
      )}

      {dashboardFilterNote && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            px: 2.5,
            backgroundColor: "#f9fafb",
            borderRadius: 1,
            borderTop: "1px solid #e0e0e0",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "#1976d2" }}
            fontWeight={600}
          >
            {dashboardFilterNote}
          </Typography>
        </Box>
      )}

      {missingDataWarning && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            px: 2.5,
            backgroundColor: "#f9fafb",
            borderRadius: 1,
            borderTop: "1px solid #e0e0e0",
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="warning.main" fontWeight={500}>
            {missingDataWarning}
          </Typography>
        </Box>
      )}
      {showChartN && chartNValue != null && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            px: 2.5,
            backgroundColor: "#f9fafb",
            borderRadius: 1,
            borderTop: "1px solid #e0e0e0",
            flexShrink: 0,
          }}
        >
          <Typography
            component="figcaption"
            variant="caption"
            color="text.secondary"
            fontWeight={500}
          >
            Chart N: {chartNValue.toLocaleString()}
          </Typography>
        </Box>
      )}
      {isStatTestingEnabled &&
        (() => {
          const hasSignificantChanges = (() => {
            if (config?.series) {
              return config.series.some((series: any) => {
                if (
                  series.statSignificance !== 0 &&
                  series.statSignificance !== undefined
                ) {
                  return true;
                }
                if (Array.isArray(series.data)) {
                  return series.data.some(
                    (point: any) =>
                      typeof point === "object" &&
                      point.statSignificance !== 0 &&
                      point.statSignificance !== undefined,
                  );
                }
                return false;
              });
            }
            return false;
          })();

          const significanceLevel =
            config?.statisticalTesting?.significanceLevel || "90%";
          const isRollingAverage =
            config?.rollingAverage?.enabled &&
            config.rollingAverage.windowSize !== undefined &&
            config.rollingAverage.windowSize > 0;
          // Use effectiveRollingPeriodCount (N = actual periods with data) if available,
          // otherwise fall back to configured windowSize
          const windowSize =
            config?.effectiveRollingPeriodCount ??
            config?.rollingAverage?.windowSize;
          const periodType = config?.timePeriodSelection?.timePeriod
            ? getPeriodTypeLabel(config.timePeriodSelection.timePeriod)
            : "period";
          const comparisonLabel = isRollingAverage
            ? `Previous Rolling Window`
            : `Previous Period`;
          const comparisonDescription = isRollingAverage
            ? `previous overlapping ${windowSize}-${periodType} rolling average`
            : `previous period`;

          if (hasSignificantChanges) {
            return (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  px: 2.5,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  <span className="text-[#10B981]">▲</span> /{" "}
                  <span className="text-[#EF4444]">▼</span> {comparisonLabel}:
                  Significant increase/decrease compared to{" "}
                  {comparisonDescription} at {significanceLevel} level of
                  significance.
                </Typography>
              </Box>
            );
          } else {
            return (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  px: 2.5,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  No statistically significant differences found compared to{" "}
                  {comparisonDescription} at {significanceLevel} level of
                  significance.
                </Typography>
              </Box>
            );
          }
        })()}
    </div>
  );
};

export default ConfigurableBarChart;
